import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, Col, Modal, ModalProps, Popover, Row, Space, Table, Popconfirm } from 'antd';
import { useTranslate } from '@tolgee/react';
import styled from '@emotion/styled';
import { css } from '@emotion/css';
import useShopeeAuthShops from 'hooks/business/authShops/useShopeeAuthShops';
import { upperCaseRegionRegionMap } from 'services/config/region';
import {
  CheckCircleFilled,
  CheckCircleOutlined,
  CloseCircleFilled,
  CloseCircleOutlined,
  CloseOutlined,
  DownloadOutlined,
  ExclamationCircleOutlined,
  PlayCircleOutlined,
  RightCircleOutlined,
  SyncOutlined,
} from '@ant-design/icons';
import { isNumber } from 'lodash-es';
import Checkbox from 'antd/es/checkbox/Checkbox';
import useAuthShopUtils, { UserRetryOperation } from 'hooks/business/authShops/useShopeeUtils';
import chatttSDK from 'services/chattt';
import { useGetRegionLabel } from 'hooks/business/useRegion';

const SuccessIcon = styled(CheckCircleFilled)`
  margin-right: 5px;
  color: var(--ant-success-color);
`;
const FailIcon = styled(CloseCircleFilled)`
  margin-right: 5px;
  color: var(--ant-error-color);
`;
const TopInfo = styled.div`
  margin-bottom: 18px;
`;
const Flex = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 12px 0;
  margin-bottom: 12px;
  border-bottom: 1px solid #f0f2f5;
`;
const Tips = styled.div`
  display: flex;
  padding: 8px 16px;
  background: #f0f4ff;
  align-items: center;
`;
const AuthAmount = styled.span`
  margin-right: 16px;
  .anticon {
    margin-right: 5px;
  }
`;
const SubText = styled.div`
  color: rgba(0, 0, 0, 0.45);
  font-size: 14px;
`;
const modalCls = css`
  .ant-modal-close {
    right: 24px;
  }
  .ant-modal-close-x {
    width: unset;
  }
`;

interface Props extends ModalProps {
  onFailedRetry?: (list: Array<RPA.Shopee.AuthAgentJSON['info']>) => void;
  onFailedExport?: (list: Array<RPA.Shopee.AuthAgentJSON>) => void;
  isConfigService: boolean;
  onConfigService: (e: any) => void;
}

const MultipleAuthResultModal = (props: Props) => {
  const { onCancel, onFailedRetry, onFailedExport, visible, isConfigService, onConfigService } =
    props;
  const { t } = useTranslate();
  const getRegionLabel = useGetRegionLabel();
  const [isOnlyFailed, setIsOnlyFailed] = useState(false);
  const { taskId, reset, authResult, authProgressInfo, isQuickMode, setShopeeAuthShopsResult } =
    useShopeeAuthShops();
  const { getAuthDescription } = useAuthShopUtils();
  const [retrySubId, setRetrySubId] = useState<string>('');

  useEffect(() => {
    const messageHandler = (evt: MessageEvent) => {
      const { event: eventName, payload } = evt.data;
      if ((eventName as RPA.Shopee.DispatchToWebMessageHooks) === 'shopAuth:retry') {
        const { data } = payload;
        const result = [...authResult];
        const index = result.findIndex((r) => r.id === data.id);
        if (index > -1) {
          const oldItem = result[index];
          result.splice(index, 1, {
            ...oldItem,
            ...data,
          });
          setShopeeAuthShopsResult(result);
        }
        setRetrySubId('');
      }
    };

    window.addEventListener('message', messageHandler, false);
    return () => {
      window.removeEventListener('message', messageHandler, false);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authResult]);

  const columns = useMemo(() => {
    return [
      {
        title: t('ViewShops.shopee.multi.interrupt.modal.result.col.account'),
        dataIndex: 'info',
        key: 'info.account',
        render: (_: any, record: RPA.Shopee.AuthAgentJSON) => {
          return record?.info?.account || '-';
        },
      },
      {
        title: t('ViewShops.shopee.multi.interrupt.modal.result.col.region'),
        dataIndex: 'info',
        key: 'info.region',
        render: (_: any, record: RPA.Shopee.AuthAgentJSON) => {
          if (record?.info?.region) {
            const region =
              upperCaseRegionRegionMap[record?.info?.region as General.UpperCaseRegion];
            return getRegionLabel(region);
          }
          return '-';
        },
      },
      {
        title: t('ViewShops.shopee.multi.interrupt.modal.result.col.stores'),
        dataIndex: 'info.shopNames',
        render: (_: any, record: RPA.Shopee.AuthAgentJSON) => {
          const result = record.authResult?.CHAT || record.authResult?.ERP;
          if (result?.authedShops) {
            const hide = result?.authedShops.map((shop: any) => <span>{shop.name}</span>);
            if (result.authedShops.length <= 3) {
              return <Space size="middle">{hide}</Space>;
            }
            const slice3 = hide.splice(0, 3);
            return (
              <>
                <Space size="middle">{slice3}</Space>
                {hide.length > 0 && (
                  <Row justify="end">
                    <Col>
                      <Popover content={<Space size="middle">{hide}</Space>}>
                        <Button type="link">
                          {t('ViewShops.shopee.multi.interrupt.modal.result.col.stores.text', {
                            x: hide.length,
                          })}
                        </Button>
                      </Popover>
                    </Col>
                  </Row>
                )}
              </>
            );
          }
          return '-';
        },
      },
      {
        title: t('ViewShops.shopee.multi.interrupt.modal.result.col.result'),
        dataIndex: 'status',
        width: 180,
        render: (_: any, record: RPA.Shopee.AuthAgentJSON) => {
          if (record) {
            const { status } = record;
            if (status === 'DoneWithFinishAll' || status === 'DoneWithSameOrgAuth') {
              return (
                <>
                  <SuccessIcon />
                  {t('ViewShops.shopee.multi.interrupt.modal.result.status.success')}
                </>
              );
            }
            return (
              <>
                <FailIcon />
                {t('ViewShops.shopee.multi.interrupt.modal.result.status.failed')}
              </>
            );
          }
          return '-';
        },
      },
      {
        title: t('ViewShops.shopee.multi.interrupt.modal.result.col.desc'),
        key: 'desc',
        render: (_: any, record: RPA.Shopee.AuthAgentJSON) => {
          const desc = getAuthDescription(record);
          return (
            <>
              <div>{desc.title || t('ViewShops.shopee.multi.auth.errors.fallback')}</div>
              {desc.subTitle && <SubText>{desc.subTitle}</SubText>}
            </>
          );
        },
      },
      {
        title: t('操作'),
        key: 'operation',
        render: (_: any, record: RPA.Shopee.AuthAgentJSON) => {
          const { id } = record;
          const { userRetryOperation } = getAuthDescription(record);

          if (userRetryOperation === UserRetryOperation.None) return '-';

          let text;
          if (userRetryOperation === UserRetryOperation.Captcha) {
            text = '发送验证码';
          }
          if (userRetryOperation === UserRetryOperation.Retry) {
            text = '重新授权';
          }

          return text ? (
            <Button
              type="link"
              loading={retrySubId === id}
              disabled={!!(retrySubId && retrySubId !== id)}
              onClick={async () => {
                setRetrySubId(id);
                await chatttSDK?.shopee.multiAuth('retry', {
                  taskId,
                  subTaskId: id,
                });
              }}
            >
              {text}
            </Button>
          ) : (
            '-'
          );
        },
      },
    ];
  }, [getAuthDescription, retrySubId, taskId, t, getRegionLabel]);

  const getFailData = (dataSource: RPA.Shopee.AuthAgentJSON[]) => {
    return dataSource.filter((data) => {
      const { status } = data;
      return status !== 'DoneWithFinishAll' && status !== 'DoneWithSameOrgAuth';
    });
  };

  const failData = useMemo(() => getFailData(authResult), [authResult]);

  const closeModal = useCallback(
    (e: any) => {
      reset();
      onCancel && onCancel(e);

      if (isConfigService && onConfigService) {
        onConfigService(e);
      }
    },
    [isConfigService, onCancel, onConfigService, reset],
  );

  const dataSource = useMemo(() => {
    if (isOnlyFailed) {
      return failData;
    }
    return authResult;
  }, [isOnlyFailed, authResult, failData]);

  const closeIcon = useMemo(() => {
    if (failData?.length === dataSource.length) {
      return <CloseOutlined onClick={closeModal} />;
    }
    if (failData?.length > 0) {
      return (
        <Popconfirm
          onConfirm={closeModal}
          title={
            <>
              <div>关闭后，授权结果将被清空</div>
              <div>你确定要关闭吗？</div>
            </>
          }
        >
          {isConfigService ? (
            <Button type="primary">{t('ViewShops.关闭并配置店铺组')}</Button>
          ) : (
            <CloseOutlined />
          )}
        </Popconfirm>
      );
    }
    return isConfigService ? (
      <CloseOutlined onClick={closeModal} />
    ) : (
      <Button type="primary" onClick={closeModal}>
        {t('ViewShops.关闭并配置店铺组')}
      </Button>
    );
  }, [failData?.length, dataSource.length, closeModal, t, isConfigService]);

  return (
    <Modal
      {...props}
      title={t('ViewShops.store.auth.modal.title')}
      width="75%"
      className={modalCls}
      visible={visible}
      footer={null}
      closeIcon={closeIcon}
      destroyOnClose
    >
      <TopInfo>
        {failData?.length > 0 ? (
          <>
            <div>
              <Tips>
                <ExclamationCircleOutlined
                  style={{ marginRight: 8, color: `var(--ant-warning-color)` }}
                />
                <div>{t('ViewShops.shopee.auth.progress.only.fail.result.fail.tips')}</div>
              </Tips>
            </div>
            <Flex>
              <Space>
                <Checkbox
                  checked={isOnlyFailed}
                  onChange={(e) => setIsOnlyFailed(e.target.checked)}
                >
                  {t('ViewShops.shopee.auth.progress.only.fail.result')}
                </Checkbox>
                <Button
                  icon={<SyncOutlined />}
                  type="primary"
                  onClick={() => {
                    onFailedRetry && onFailedRetry(failData.map((r) => r.info));
                  }}
                >
                  {t('ViewShops.shopee.multi.interrupt.modal.result.fail.retry')}
                </Button>
              </Space>
              <Button
                icon={<DownloadOutlined />}
                onClick={() => {
                  onFailedExport && onFailedExport(failData);
                }}
              >
                {t('ViewShops.shopee.multi.interrupt.modal.result.fail.export')}
              </Button>
            </Flex>
          </>
        ) : null}
        <div>
          <AuthAmount>
            <RightCircleOutlined style={{ color: 'var(--ant-primary-color)' }} />
            {t('ViewShops.shopee.auth.progress.account.total.title', {
              x: isNumber(authProgressInfo?.allAccounts) ? authProgressInfo?.allAccounts : '-',
            })}
          </AuthAmount>
          <AuthAmount>
            <CheckCircleOutlined style={{ color: 'var(--ant-success-color)' }} />
            {t('ViewShops.shopee.auth.progress.account.success.title', {
              x: isNumber(authProgressInfo?.authSuccessAccounts)
                ? authProgressInfo?.authSuccessAccounts
                : '-',
            })}
          </AuthAmount>
          {isQuickMode ? (
            <AuthAmount>
              <PlayCircleOutlined style={{ color: 'var(--ant-info-color)' }} />
              {t('ViewShops.shopee.auth.progress.account.quick.skip.title', {
                x: isNumber(authProgressInfo?.quickModeAccounts)
                  ? authProgressInfo?.quickModeAccounts
                  : '-',
              })}
            </AuthAmount>
          ) : null}
          <AuthAmount>
            <CloseCircleOutlined style={{ color: 'var(--ant-error-color)' }} />
            {t('ViewShops.shopee.auth.progress.account.fail.title', {
              x: isNumber(authProgressInfo?.authFailAccounts)
                ? authProgressInfo?.authFailAccounts
                : '-',
            })}
          </AuthAmount>
        </div>
      </TopInfo>
      <Table columns={columns} dataSource={dataSource} />
    </Modal>
  );
};

export default MultipleAuthResultModal;
